import type { LaptopInformation, LaptopScores } from "./types";
import { APP_GAME_LABELS } from "./apps-games-catalog";
import { cheapestSellerPrice, displayPrice, formatMoney } from "./amazon";

// Plain-English explanations of why a laptop scored the way it did against a
// user's questionnaire answers. Deliberately free of React and DOM APIs: the
// score modal renders these in the browser, and the PDF report draws the same
// strings onto a page, so both surfaces always say the same thing.

export type Answer = string | string[] | number | boolean | null;
export type Reference = Record<string, number | string | null>;

// ── Labels ────────────────────────────────────────────────────────────────────

const USAGE_LABELS: Record<string, string> = {
  desk: "desk-based", hybrid: "mixed", mobile: "on-the-go",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

export function currencySymbol(country: string | undefined): string {
  return country === "us" ? "$" : "£";
}

export function getAppNames(answers: Record<string, Answer>): string {
  const sw = (answers["software"] as string[] | undefined) ?? [];
  const gm = (answers["games"] as string[] | undefined) ?? [];
  const names = [
    ...sw.map((s) => APP_GAME_LABELS[s] ?? s),
    ...gm.map((g) => APP_GAME_LABELS[g] ?? g),
  ];
  if (names.length === 0) return "your apps";
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

export function ordinal(n: number): string {
  const m100 = n % 100, m10 = n % 10;
  const sfx = m100 >= 11 && m100 <= 13 ? "th" : m10 === 1 ? "st" : m10 === 2 ? "nd" : m10 === 3 ? "rd" : "th";
  return `${n}${sfx}`;
}

export function scoreQuality(s: number): string {
  if (s >= 90) return "excellent";
  if (s >= 75) return "very good";
  if (s >= 60) return "good";
  if (s >= 40) return "decent";
  return "limited";
}

/**
 * How well a spec meets the user's ideal, 0-100.
 *
 * Meeting or beating the ideal is a full match. This used to be a symmetric
 * distance, which punished having *more* than you need: a 1TB drive against a
 * 512GB ideal came out at 5% — "limited storage match" — while the sentence
 * beside it correctly said it "meets or exceeds the ideal". More storage than
 * you asked for is not a worse laptop, so only shortfalls are scored down.
 */
export function computeCloseness(actual: number, ref: number): number | null {
  if (!ref || ref <= 0) return null;
  if (actual >= ref) return 100;
  return Math.round(Math.max(0, actual / ref) * 100);
}

function metricRank(myScore: number | null, allScores: (number | null)[]): number {
  if (myScore === null) return allScores.length;
  const sorted = [...allScores].sort((a, b) => (b ?? -1) - (a ?? -1));
  const idx = sorted.findIndex((s) => s === myScore);
  return idx === -1 ? allScores.length : idx + 1;
}

export type ExplanationOptions = {
  /**
   * Append "Among your N recommendations, this ranks 3rd for X." to each row.
   *
   * On by default, and on in the score modal, where the user is looking at those
   * very recommendations. The PDF turns it off: repeated on six rows of a
   * standalone document it reads as filler, and it refers to a shortlist the
   * person holding the PDF may never have seen.
   */
  includeRank?: boolean;
};

/** The trailing "…this ranks Nth for X." sentence, or "" when suppressed. */
function rankSentence(
  score: number | null,
  allScores: (number | null)[],
  noun: string,
  opts?: ExplanationOptions,
): string {
  if (opts?.includeRank === false) return "";
  const total = allScores.length;
  if (total <= 1) return "";
  return ` Among your ${total} recommendations, this ranks ${ordinal(metricRank(score, allScores))} for ${noun}.`;
}

export function formatStorage(gb: number): string {
  return gb >= 1000 ? `${gb / 1000}TB` : `${gb}GB`;
}

export function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function scoreColor(score: number): string {
  // Darkened traffic-light palette so white-on-colour badges and colour-as-text
  // both clear WCAG AA (≈4.5:1) against white.
  //
  // A sub-score is a match-to-target, not an absolute grade: because the target is
  // re-anchored to a top-in-budget machine, even a genuinely strong laptop sits in
  // the 60-90% band. So "good" (green) starts at 55 — matching scoreBand's "Good
  // match" — rather than 75, which made a good 60-90% performance read as a
  // mediocre yellow. emerald-700 clears WCAG AA as both text and background.
  if (score >= 55) return "#047857"; // emerald-700 — good → strong → near-perfect
  if (score >= 40) return "#a16207"; // yellow-700 — decent
  if (score >= 25) return "#c2410c"; // orange-700
  return "#dc2626"; // red-600
}

export function scoreBand(score: number): string {
  if (score >= 88) return "Near perfect match";
  if (score >= 72) return "Very strong match";
  if (score >= 55) return "Good match";
  if (score >= 40) return "Decent match";
  return "Partial match";
}

// The rank sentences compare this laptop against the user's other results. On a
// /spec/ page the laptop the user landed on is usually absent from their top 5,
// and metricRank falls back to "last" whenever it cannot find the score — so
// every sentence would claim the laptop ranks bottom. Splicing it into the set
// (carrying the scores we just fetched for it, since the /spec/ payload has
// scores: null) makes the comparison honest. In the recommendations flow the
// laptop is already in allItems, so this is a no-op.
export function buildComparisonSet(
  item: LaptopInformation,
  scores: LaptopScores,
  allItems: LaptopInformation[],
): LaptopInformation[] {
  const withScores = item.scores ? item : { ...item, scores };
  const out = [withScores];
  for (const other of allItems) {
    if (other.laptop.id !== item.laptop.id) out.push(other);
  }
  return out;
}

// allItems may contain entries whose scores came back null (the /spec/ payload
// carries scores: null), so every lookup here is defensive.
function scoresOf(l: LaptopInformation): Partial<LaptopScores> {
  return l.scores ?? {};
}

// ── Text generators ───────────────────────────────────────────────────────────

export function genPerformanceText(
  score: number,
  item: LaptopInformation,
  allItems: LaptopInformation[],
  reference: Reference,
  answers: Record<string, Answer>,
  opts?: ExplanationOptions,
): string {
  const appNames = getAppNames(answers);
  const quality = scoreQuality(score);
  // cinebench_2024 is the key buildRecommendationQuery actually emits, and the
  // metric the backend scores performance on (DISPLAY_GROUPS["performance"]).
  const refPerf = reference["cinebench_2024"] as number | null;
  const actual = item.cpu.cinebench_2024 ?? 0;

  const cpuName = item.cpu.name;
  const gpuName = item.gpu.name;
  const hasGames = ((answers["games"] as string[] | undefined) ?? []).length > 0;
  const hasCreative = ((answers["software"] as string[] | undefined) ?? []).some(
    (s) => ["premiere", "blender", "photoshop"].includes(s),
  );

  let deltaText = "";
  if (refPerf && refPerf > 0) {
    const diff = actual - refPerf;
    const pct = Math.round((Math.abs(diff) / refPerf) * 100);
    // "It", not the CPU name again: the name is already in the opening clause,
    // and repeating it reads badly with the long ones ("The Qualcomm Snapdragon
    // X Elite X1E-78-100. The Qualcomm Snapdragon X Elite X1E-78-100 scores…").
    if (diff > refPerf * 0.1) {
      deltaText = ` It scores ${pct}% above the threshold needed for ${appNames}, so you'll have plenty of headroom for demanding tasks.`;
    } else if (diff < -(refPerf * 0.1)) {
      deltaText = ` It scores ${pct}% below the ideal for ${appNames}, which may limit performance on heavier workloads.`;
    } else {
      deltaText = ` It closely matches what ${appNames} requires.`;
    }
  } else {
    // No reference figure to compare against, and the opening sentence already
    // names the CPU — so say nothing rather than repeat it.
    deltaText = "";
  }

  // A missing benchmark is not a score of zero. Only quote a figure when we have
  // one; otherwise describe the GPU without inventing a number.
  const gpuGaming = item.gpu.review_gaming;
  let gpuText = "";
  if (hasGames && gpuGaming != null) {
    gpuText = ` The ${gpuName} has ${scoreQuality(gpuGaming)} gaming performance (${gpuGaming}/100), which directly contributes to your gaming experience.`;
  } else if (hasGames) {
    gpuText = ` Graphics are handled by the ${gpuName}.`;
  } else if (hasCreative) {
    gpuText = ` The ${gpuName} also contributes to GPU-accelerated tasks like rendering and image processing.`;
  } else {
    gpuText = ` The ${gpuName} handles graphics duties alongside the CPU.`;
  }

  const rankText = rankSentence(
    score,
    allItems.map((l) => scoresOf(l).performance ?? null),
    "performance",
    opts,
  );

  return `This laptop has ${quality} performance (${score}%) for ${appNames}, powered by the ${cpuName}.${deltaText}${gpuText}${rankText}`;
}

export function genBatteryText(
  score: number,
  item: LaptopInformation,
  allItems: LaptopInformation[],
  answers: Record<string, Answer>,
  opts?: ExplanationOptions,
): string {
  const usage = (answers["usage"] as string | undefined) ?? "hybrid";
  const usageLabel = USAGE_LABELS[usage] ?? "mixed";
  const quality = scoreQuality(score);

  let batteryDetail = "";
  if (score >= 80) batteryDetail = "Battery life is excellent. This laptop can easily handle full-day use without a charge.";
  else if (score >= 60) batteryDetail = "Battery life is solid for most days.";
  else if (score >= 40) batteryDetail = "Battery life is decent but you may need a midday charge on heavy days.";
  else batteryDetail = "Battery life may fall short for extended away-from-charger sessions.";

  // GPU energy efficiency contribution.
  //
  // Only spoken to when we actually have the benchmark. Previously a null was
  // coerced to 0, so a GPU we simply have no data for was reported as "draws
  // more power (efficiency: 0/100)" — a fabricated figure, and one that flatly
  // contradicted the "battery life is excellent" sentence right before it.
  const gpuName = item.gpu.name;
  const gpuEff = item.gpu.review_energy_efficiency;
  let gpuNote = "";
  if (gpuEff != null) {
    const gpuEffQuality = scoreQuality(gpuEff);
    if (gpuEff >= 75) {
      gpuNote = ` The ${gpuName} is also ${gpuEffQuality} on power draw (${gpuEff}/100), helping extend battery life under load.`;
    } else if (gpuEff >= 50) {
      gpuNote = ` The ${gpuName} has ${gpuEffQuality} energy efficiency (${gpuEff}/100), which has a moderate impact on battery life.`;
    } else {
      gpuNote = ` The ${gpuName} draws more power (efficiency: ${gpuEff}/100), which can reduce battery life during GPU-heavy tasks.`;
    }
  }

  let usageNote = "";
  if (usage === "mobile") usageNote = " Since you're always on the move, battery efficiency was weighted heavily in your score.";
  else if (usage === "desk") usageNote = " Since you mainly use at a desk, battery was a lower priority in your score.";

  const rankText = rankSentence(
    score,
    allItems.map((l) => scoresOf(l).battery_life ?? null),
    "battery & efficiency",
    opts,
  );

  return `${capitalize(quality)} battery & efficiency (${score}%) for your ${usageLabel} usage. ${batteryDetail}${gpuNote}${usageNote}${rankText}`;
}

export function genRamText(
  ramScore: number,
  item: LaptopInformation,
  allItems: LaptopInformation[],
  reference: Reference,
  answers: Record<string, Answer>,
  opts?: ExplanationOptions,
): string {
  const actualRam = item.specs.ram.size_gb;
  const refRam = reference["ram_size_gigabytes"] as number | null;
  const appNames = getAppNames(answers);
  const quality = scoreQuality(ramScore);

  let deltaText = "";
  if (refRam && refRam > 0) {
    if (actualRam >= refRam) {
      deltaText = ` With ${actualRam}GB, this meets or exceeds the ideal ${refRam}GB for ${appNames}, with plenty of headroom for multitasking.`;
    } else {
      const diff = refRam - actualRam;
      deltaText = ` With ${actualRam}GB, this is ${diff}GB below the ideal ${refRam}GB for ${appNames}, which may limit heavy multitasking over time.`;
    }
  }

  const rankText = rankSentence(
    ramScore,
    allItems.map((l) => (refRam ? computeCloseness(l.specs.ram.size_gb, refRam) : null)),
    "RAM match",
    opts,
  );

  return `This laptop has ${quality} RAM match (${ramScore}%) for ${appNames}.${deltaText}${rankText}`;
}

export function genStorageText(
  storageScore: number,
  item: LaptopInformation,
  allItems: LaptopInformation[],
  reference: Reference,
  answers: Record<string, Answer>,
  opts?: ExplanationOptions,
): string {
  const actualStorage = item.specs.storage.size_gb;
  const refStorage = reference["storage_size_gigabytes"] as number | null;
  const appNames = getAppNames(answers);
  const quality = scoreQuality(storageScore);

  let deltaText = "";
  if (refStorage && refStorage > 0) {
    if (actualStorage >= refStorage) {
      deltaText = ` With ${formatStorage(actualStorage)}, this meets or exceeds the ideal ${formatStorage(refStorage)} for ${appNames}.`;
    } else {
      deltaText = ` With ${formatStorage(actualStorage)}, this is below the ideal ${formatStorage(refStorage)} for ${appNames}, which may mean less space for your files over time.`;
    }
  }

  const rankText = rankSentence(
    storageScore,
    allItems.map((l) => (refStorage ? computeCloseness(l.specs.storage.size_gb, refStorage) : null)),
    "storage match",
    opts,
  );

  return `This laptop has ${quality} storage match (${storageScore}%) for ${appNames}.${deltaText}${rankText}`;
}

// The budget line used to mix two different prices: the `withinBudget` flag comes
// from the cheapest seller, while the price it quoted was the Amazon listing we
// link to. On a laptop where those differ it produced flat contradictions —
// "within your £1,200 budget, priced at £1,200.17".
//
// Now over/under is always derived from the price we actually quote, and when the
// laptop only fits the budget via a cheaper seller, that is said out loud instead
// of being papered over.
export function genBudgetText(
  withinBudget: boolean,
  item: LaptopInformation,
  answers: Record<string, Answer>,
): string {
  const country = answers["country"] as string | undefined;
  const sym = currencySymbol(country);
  const budget = answers["budget"];
  const priced = displayPrice(item, country);

  if (!budget && budget !== 0) {
    return withinBudget
      ? "This laptop is within your budget."
      : "This laptop is above your budget.";
  }

  const budgetNum = Number(budget);
  const money = (v: number, s = sym) => formatMoney(v, s);

  // Most configs have no price of their own and no priced seller, so say what we
  // know rather than inventing a number.
  if (!priced) {
    return withinBudget
      ? `This laptop is within your ${money(budgetNum)} budget.`
      : `This laptop is above your ${money(budgetNum)} budget.`;
  }

  // When the quoted price is for a used unit, say so in the same breath as the
  // number. A reader who assumes a refurbished price is a new-machine price has
  // been misled by the one sentence that exists to explain the price.
  const quoted = priced.refurbished
    ? `${money(priced.price, priced.sym)} (${priced.conditionLabel ?? "refurbished"})`
    : money(priced.price, priced.sym);

  if (priced.price <= budgetNum) {
    const saved = budgetNum - priced.price;
    if (saved > budgetNum * 0.1) {
      return `This laptop is within your ${money(budgetNum)} budget, priced at ${quoted}. You'd save ${money(Math.round(saved))} compared to your maximum, leaving room for accessories or an extended warranty.`;
    }
    return `This laptop is within your ${money(budgetNum)} budget, priced at ${quoted}.`;
  }

  // Quoted price is over budget, yet the laptop was flagged in-budget because
  // some seller lists it for less. Say so — but deliberately do NOT quote that
  // seller's price. Seller rows are matched to a laptop by series, so the
  // cheapest is sometimes a mismatched listing (an accessory, or a different
  // configuration) at an implausible price. Printing that number in a document
  // people forward would be worse than the contradiction this replaced.
  const cheapest = cheapestSellerPrice(item);
  if (withinBudget && cheapest && cheapest.price <= budgetNum) {
    return `The listing we link to is ${quoted}, a little over your ${money(budgetNum)} budget — though other retailers list this laptop for less. Compare them on the laptop's page before buying.`;
  }

  const over = priced.price - budgetNum;
  return `This laptop exceeds your ${money(budgetNum)} budget by ${money(Math.round(over))} (priced at ${quoted}). It may still be worth the extra spend given its overall match quality.`;
}

export function genFormFactorText(
  matches: boolean,
  item: LaptopInformation,
  answers: Record<string, Answer>,
): string {
  const screen = answers["screen"] as string | undefined;
  const actualInches = item.specs.display.inches;

  if (!screen || screen === "none") {
    return `You didn't specify a screen size preference, so this laptop's ${actualInches}" display was not penalised for size.`;
  }

  const prefInches = parseInt(screen);

  if (matches) {
    return `The ${actualInches}" display matches your preferred ${prefInches}" screen size.`;
  }

  // Rounded: the raw subtraction gives things like 0.1999999999999993.
  const diff = Math.round(Math.abs(actualInches - prefInches) * 10) / 10;
  const direction = actualInches > prefInches ? "larger" : "smaller";
  return `You preferred a ${prefInches}" screen. This laptop has a ${actualInches}" display, ${diff}" ${direction} than your preference.`;
}

export function genFutureProofText(
  score: number,
  item: LaptopInformation,
  allItems: LaptopInformation[],
  opts?: ExplanationOptions,
): string {
  const ram = item.specs.ram.size_gb;
  // Not `?? 0`: a CPU we have no multi-core benchmark for is not a CPU that
  // scored zero, and printing "score of 0/100" invents a damning figure.
  const multi = item.cpu.review_multi_core_performance;
  const yearStr = item.laptop.release_year;
  const yr = yearStr ? parseInt(String(yearStr).slice(0, 4)) : null;
  const quality = scoreQuality(score);

  let ramNote: string;
  if (ram >= 32) ramNote = `Its ${ram}GB of RAM gives it excellent headroom for future software demands.`;
  else if (ram >= 16) ramNote = `${ram}GB of RAM is solid for now, though future apps may push against this limit.`;
  else ramNote = `${ram}GB of RAM is on the lower end — software demands could outgrow this sooner.`;

  let multiNote = "";
  if (multi != null) {
    if (multi >= 80) multiNote = ` Its multi-core CPU score of ${multi}/100 means it can handle demanding workloads for years to come.`;
    else if (multi >= 55) multiNote = ` Its multi-core CPU score of ${multi}/100 is solid for most tasks over the next few years.`;
    else multiNote = ` Its multi-core CPU score of ${multi}/100 is decent, but may feel dated sooner for heavier workloads.`;
  }

  let yearNote = "";
  if (yr) {
    const age = new Date().getFullYear() - yr;
    if (age <= 1) yearNote = ` Released in ${yr}, this is a recent model and should stay well-supported for years.`;
    else if (age <= 3) yearNote = ` Released in ${yr}, it is still within active support windows for drivers and OS updates.`;
    else yearNote = ` Released in ${yr}, it is getting older — OS support and driver optimisations may wind down sooner.`;
  }

  const rankText = rankSentence(
    score,
    allItems.map((l) => scoresOf(l).future_proof ?? null),
    "future-proofing",
    opts,
  );

  return `${capitalize(quality)} future-proofing score (${score}%). ${ramNote}${multiNote}${yearNote}${rankText}`;
}

export function genValueText(
  score: number,
  item: LaptopInformation,
  allItems: LaptopInformation[],
  answers: Record<string, Answer>,
  opts?: ExplanationOptions,
): string {
  const country = answers["country"] as string | undefined;
  const priced = displayPrice(item, country);
  const cpuFinal = item.cpu.review_final_score;
  const quality = scoreQuality(score);

  const money = priced ? formatMoney(priced.price, priced.sym) : null;

  let intro: string;
  if (score >= 75) {
    intro = money
      ? `This laptop delivers strong performance for its price. You get a lot for your ${money}.`
      : "This laptop delivers strong performance for its price.";
  } else if (score >= 50) {
    intro = money
      ? `This laptop offers reasonable value at ${money}, though there may be better-value options at this price point.`
      : "This laptop offers reasonable value, though there may be better-value options at this price point.";
  } else {
    intro = money
      ? `At ${money}, you are paying a premium relative to the performance on offer.`
      : "You are paying a premium relative to the performance on offer.";
  }

  // Only quote the CPU figure when we have it — see genFutureProofText.
  const cpuNote =
    cpuFinal != null
      ? ` Its CPU overall score is ${cpuFinal}/100, and this is weighed against price to compute the value score.`
      : "";

  const rankText = rankSentence(
    score,
    allItems.map((l) => scoresOf(l).value_for_money ?? null),
    "value for money",
    opts,
  );

  return `${capitalize(quality)} value for money (${score}%). ${intro}${cpuNote}${rankText}`;
}

// ── Aggregate ─────────────────────────────────────────────────────────────────

export type MatchExplanationKey =
  | "budget" | "size" | "perf" | "battery" | "future" | "value" | "ram" | "storage";

export type MatchExplanation = {
  key: MatchExplanationKey;
  label: string;
  /** Percentage rows carry a score; the two boolean rows carry boolValue instead. */
  score: number | null;
  boolValue?: boolean;
  /**
   * What to print instead of a bare Yes/No. Set when a plain "Yes" would be
   * meaningless — a laptop cannot match a screen size the user never asked for.
   */
  boolLabel?: string;
  text: string;
};

// Every row the score modal would render, in the same order, as plain data.
export function buildMatchExplanations(
  item: LaptopInformation,
  scores: LaptopScores,
  reference: Reference,
  answers: Record<string, Answer>,
  allItems: LaptopInformation[],
  opts?: ExplanationOptions,
): MatchExplanation[] {
  const out: MatchExplanation[] = [];

  out.push({
    key: "budget",
    label: "Within budget",
    score: null,
    boolValue: scores.within_budget,
    text: genBudgetText(scores.within_budget, item, answers),
  });

  // The backend reports form_factor = true when no preference was given, which
  // rendered as "Preferred size: Yes" directly above "You didn't specify a
  // screen size preference." Say "Not specified" instead of claiming a match.
  const screen = answers["screen"] as string | undefined;
  const screenAsked = Boolean(screen) && screen !== "none";
  out.push({
    key: "size",
    label: "Preferred size",
    score: null,
    boolValue: scores.form_factor,
    boolLabel: screenAsked ? undefined : "Not specified",
    text: genFormFactorText(scores.form_factor, item, answers),
  });

  if (scores.performance !== null) {
    out.push({
      key: "perf",
      label: "Performance",
      score: scores.performance,
      text: genPerformanceText(scores.performance, item, allItems, reference, answers, opts),
    });
  }

  if (scores.battery_life !== null) {
    out.push({
      key: "battery",
      label: "Battery & efficiency",
      score: scores.battery_life,
      text: genBatteryText(scores.battery_life, item, allItems, answers, opts),
    });
  }

  if (scores.future_proof != null) {
    out.push({
      key: "future",
      label: "Future-proofing",
      score: scores.future_proof,
      text: genFutureProofText(scores.future_proof, item, allItems, opts),
    });
  }

  if (scores.value_for_money != null) {
    out.push({
      key: "value",
      label: "Value for money",
      score: scores.value_for_money,
      text: genValueText(scores.value_for_money, item, allItems, answers, opts),
    });
  }

  // RAM and storage are not read from scores.storage_and_ram: the modal shows
  // how close each one lands to the user's ideal, computed here the same way.
  const refRam = reference["ram_size_gigabytes"] as number | null;
  const refStorage = reference["storage_size_gigabytes"] as number | null;
  const ramCloseness = refRam ? computeCloseness(item.specs.ram.size_gb, refRam) : null;
  const storageCloseness = refStorage ? computeCloseness(item.specs.storage.size_gb, refStorage) : null;

  if (ramCloseness !== null) {
    out.push({
      key: "ram",
      label: "RAM",
      score: ramCloseness,
      text: genRamText(ramCloseness, item, allItems, reference, answers, opts),
    });
  }

  if (storageCloseness !== null) {
    out.push({
      key: "storage",
      label: "Storage",
      score: storageCloseness,
      text: genStorageText(storageCloseness, item, allItems, reference, answers, opts),
    });
  }

  return out;
}
